import { useParams, useNavigate, Link } from 'react-router-dom';
import { useClient, useCreateClient, useUpdateClient } from '@/hooks/useClients';
import { ClientForm } from '@/components/clients/ClientForm';
import type { ClientInput } from '@jobos/shared/types/client';

export default function EditClient() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { data: client, isLoading: clientLoading } = useClient(id);
  const { mutateAsync: createClient, isPending: isCreating } = useCreateClient();
  const { mutateAsync: updateClient, isPending: isUpdating } = useUpdateClient(id!);

  const handleSubmit = async (data: ClientInput) => {
    try {
      if (isEditMode) {
        await updateClient(data);
        navigate(`/clients/${id}`);
      } else {
        const newClient = await createClient(data);
        navigate(`/clients/${newClient.id}`);
      }
    } catch (error) {
      console.error('Failed to save client:', error);
    }
  };

  if (clientLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  const isPending = isCreating || isUpdating;

  // Convert client data to ClientInput format (null -> undefined)
  const defaultValues = client
    ? {
        name: client.name ?? undefined,
        firstName: client.firstName ?? undefined,
        lastName: client.lastName ?? undefined,
        managerName: client.managerName ?? undefined,
        contactInfo: client.contactInfo ?? undefined,
        overdueAfterDays: client.overdueAfterDays ?? undefined,
        // Without this the form would open with an empty AP list and save it
        // back as [], wiping the client's addresses on any unrelated edit.
        apEmails: client.apEmails ?? [],
      }
    : undefined;

  return (
    <div>
      <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={isEditMode ? `/clients/${id}` : '/clients'}
            className="text-sm text-blue-600 hover:text-blue-800 mb-2 inline-block"
          >
            ← Back
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Client' : 'Create New Client'}
          </h1>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <ClientForm
            isEdit={isEditMode}
            onSubmit={handleSubmit}
            defaultValues={defaultValues}
            isPending={isPending}
            submitButtonText={isEditMode ? 'Update Client' : 'Create Client'}
            onCancel={() => navigate(isEditMode ? `/clients/${id}` : '/clients')}
          />
        </div>
      </div>
    </div>
  );
}
